import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MEDIA } from "@/lib/media";

const POSTS = [140, 260, 380, 500, 620, 740, 860];

const Post = ({ progress, i }) => {
  const start = 0.06 + (i / POSTS.length) * 0.26;
  const scaleY = useTransform(progress, [start, start + 0.14], [0, 1]);
  const opacity = useTransform(progress, [start, start + 0.05], [0, 1]);
  return (
    <motion.rect
      x={POSTS[i] - 4} y={150} width={8} height={172} rx={2}
      fill="#E8E6E1"
      style={{ scaleY, opacity, transformBox: "fill-box", transformOrigin: "50% 100%" }}
    />
  );
};

const Panel = ({ progress, i }) => {
  const start = 0.5 + (i / (POSTS.length - 1)) * 0.28;
  const opacity = useTransform(progress, [start, start + 0.12], [0, 0.5]);
  const scaleY = useTransform(progress, [start, start + 0.12], [0.2, 1]);
  return (
    <motion.rect
      x={POSTS[i] + 6} y={165} width={POSTS[i + 1] - POSTS[i] - 12} height={150} rx={2}
      fill="url(#glass)" stroke="rgba(196,164,132,0.5)" strokeWidth={1}
      style={{ opacity, scaleY, transformBox: "fill-box", transformOrigin: "50% 100%" }}
    />
  );
};

export const RailingAssembly = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const railLen = useTransform(scrollYProgress, [0.3, 0.52], [0, 1]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  const l1 = useTransform(scrollYProgress, [0.05, 0.12, 0.3, 0.36], [0, 1, 1, 0]);
  const l2 = useTransform(scrollYProgress, [0.32, 0.4, 0.5, 0.56], [0, 1, 1, 0]);
  const l3 = useTransform(scrollYProgress, [0.55, 0.63, 0.95, 1], [0, 1, 1, 1]);

  return (
    <section ref={ref} className="relative bg-onyx-black" style={{ height: "280vh" }} data-testid="railing-assembly">
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col justify-center">
        <motion.img style={{ y: bgY }} src={MEDIA.darkGlassStair} alt="" className="absolute inset-0 w-full h-[112%] object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />

        <div className="relative max-w-[1400px] mx-auto w-full px-6 lg:px-12">
          <span className="text-onyx-wood font-mono text-xs tracking-[0.3em] uppercase">How we build it</span>
          <div className="relative h-16 mt-4 mb-8">
            {[["01 — Posts & Anchors", "Structural posts are set and anchored to fractions of an inch.", l1],
              ["02 — Continuous Handrail", "A seamless top rail is run and welded into one clean line.", l2],
              ["03 — Glass Infill", "Tempered panels drop in — transparent, secure, resolved.", l3]].map(([t, d, o], k) => (
              <motion.div key={k} style={{ opacity: o }} className="absolute inset-0">
                <h2 className="text-white font-display font-extrabold text-3xl md:text-5xl tracking-tightest">{t}</h2>
                <p className="text-white/60 mt-2 md:text-lg">{d}</p>
              </motion.div>
            ))}
          </div>

          <svg viewBox="0 0 1000 360" className="w-full h-[42vh]" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="glass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(196,224,230,0.55)" />
                <stop offset="100%" stopColor="rgba(160,180,190,0.15)" />
              </linearGradient>
            </defs>
            {/* floor line */}
            <line x1="60" y1="322" x2="940" y2="322" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
            {POSTS.map((_, i) => <Post key={i} progress={scrollYProgress} i={i} />)}
            {POSTS.slice(0, -1).map((_, i) => <Panel key={i} progress={scrollYProgress} i={i} />)}
            {/* handrail */}
            <motion.path
              d="M120 150 L880 150" fill="none" stroke="#C4A484" strokeWidth="7" strokeLinecap="round"
              style={{ pathLength: railLen }}
            />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default RailingAssembly;
